import { useState, useRef, useEffect, lazy } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Button,
    ButtonBase,
    CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';
import Badge from '@mui/material/Badge';
// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import Loadable from 'ui-component/Loadable';
// import NotificationList from './NotificationList';
// firebase
import { db } from 'services/firebase';
import { MLog } from 'services/FuncionesDev';
// user context
import {
    UseUserState,
    UseUserDispatch,
    AddNotification,
    DeleteNotification,
    AcceptAll,
    SetNotification,
    SetContadorBandeja,
    AddAllNotificacion
} from 'context/UserContext';
// assets
import { IconBell } from '@tabler/icons';

// notification status options
const status = [
    {
        value: 'all',
        label: 'All Notification'
    },
    {
        value: 'new',
        label: 'New'
    },
    {
        value: 'unread',
        label: 'Unread'
    },
    {
        value: 'other',
        label: 'Other'
    }
];
const NotificationList = Loadable(lazy(() => import('./NotificationList')));
// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const { datos, notificacionesContext, bandejasAsignadas } = UseUserState();
    const theme = useTheme();
    const userDispatch = UseUserDispatch();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleChange = (event) => {
        if (event?.target.value) setValue(event?.target.value);
    };

    useEffect(() => {
        // leer todas las notificaiones
        db.read.once(`notificacion_empresa/${datos.id_empresa}/creado`, (data) => {
            let ultima = '';
            const lista = [];
            data.forEach((item, i) => {
                // console.log('item', item.val());
                lista.push({ ...item.val(), key: item.key });
                ultima = item.key;
            });
            AddAllNotificacion(userDispatch, lista);
            const nuevos = db.query(db.ref(`notificacion_empresa/${datos.id_empresa}/creado`), db.startAfter(ultima));
            db.onChildAdded(nuevos, (data) => {
                AddNotification(userDispatch, { ...data.val(), key: data.key });
            });
            // eliminar notifiacion
            db.onChildRemoved(db.ref(`notificacion_empresa/${datos.id_empresa}/creado`), (data) => {
                DeleteNotification(userDispatch, { ...data.val(), key: data.key });
            });
        });

        if (datos.configuraciones.ChatCenter !== undefined && bandejasAsignadas.length > 0) {
            datos.configuraciones.ChatCenter.bots.forEach((bot, i) => {
                datos.configuraciones.ChatCenter.redes.forEach((redes, i) => {
                    bandejasAsignadas.forEach((item, i) => {
                        db.read.on(`bandeja_usuario/${datos.id_empresa}/${redes.id}/${bot.id}/${item}`, (snap) => {
                            SetContadorBandeja(userDispatch, snap, { red: redes.id, bot: bot.id, id: snap.key }, item);
                        });
                    });
                });
            });
        }
        // desmontar componente
        return () => {
            db.off(db.ref(`notificacion_empresa/${datos.id_empresa}/creado`));
            db.off(db.ref(`bandeja_usuario/${datos.id_empresa}`));
            SetNotification(userDispatch, []);
        };
    }, [datos, bandejasAsignadas]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <ButtonBase sx={{ borderRadius: '12px' }}>
                    <Badge badgeContent={notificacionesContext.length} color="secondary">
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                transition: 'all .2s ease-in-out',
                                background: theme.palette.secondary.light,
                                color: theme.palette.secondary.dark,
                                '&[aria-controls="menu-list-grow"],&:hover': {
                                    background: theme.palette.secondary.dark,
                                    color: theme.palette.secondary.light
                                }
                            }}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                            color="inherit"
                        >
                            <IconBell stroke={1.5} size="1.3rem" />
                        </Avatar>
                    </Badge>
                </ButtonBase>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Grid container direction="column" spacing={2}>
                                        <Grid item xs={12}>
                                            <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                <Grid item>
                                                    <Stack direction="row" spacing={2}>
                                                        <Typography variant="subtitle1">Notificaciones</Typography>
                                                        <Chip
                                                            size="small"
                                                            label={notificacionesContext.length}
                                                            sx={{
                                                                color: theme.palette.background.default,
                                                                bgcolor: theme.palette.warning.dark
                                                            }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        size="small"
                                                        onClick={(e) => AcceptAll(userDispatch)}
                                                        disableElevation
                                                        color="primary"
                                                    >
                                                        Aceptar todas
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PerfectScrollbar
                                                style={{ height: '100%', maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden' }}
                                            >
                                                <Grid container direction="column" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box sx={{ px: 2, pt: 0.25 }}>
                                                            <TextField
                                                                id="outlined-select-currency-native"
                                                                select
                                                                fullWidth
                                                                disabled
                                                                value={value}
                                                                onChange={handleChange}
                                                                SelectProps={{
                                                                    native: true
                                                                }}
                                                            >
                                                                {status.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} p={0}>
                                                        <Divider sx={{ my: 0 }} />
                                                    </Grid>
                                                </Grid>
                                                <NotificationList />
                                            </PerfectScrollbar>
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                    <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                        <Button size="small" disableElevation disabled>
                                            View All
                                        </Button>
                                    </CardActions>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
